import React from 'react';
import {
  ArrowRight,
  Compass,
  Cpu,
  ShieldCheck,
  Sparkles,
  Network,
  Server,
  BrainCircuit,
  CheckCircle2,
  Sun,
} from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { SectionHeader } from '../../components/common/SectionHeader';
import { SEO } from '../../components/common/SEO';

export default function TechnologyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-navy">
      <SEO
        title="技術と開発｜電力インテリジェンスの思想と技術基盤"
        description="電力の設計と制御をひとつの流れにする「電力インテリジェンス」。現場課題から生まれた『1minute solar』『SPAQ CORE』の開発背景、設計・制御・検証の3つのコア技術、今後の研究開発テーマをご紹介します。"
        canonicalPath="/technology"
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient border-b border-surface-border py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <Badge variant="cyan" className="mb-4">TECHNOLOGY & R&D</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">
              電力の設計と制御を、
              <br className="hidden sm:inline" />
              ひとつの流れにする。
            </h1>
            <p className="mt-5 text-base sm:text-lg text-navy-muted leading-relaxed max-w-3xl">
              大切なのは「設備をたくさん載せること」ではなく、「その施設の電気の使い方に合った設備を選び、導入後も賢く使いこなすこと」です。SPAQは現場のデータと設計知見をもとに、導入前の判断から導入後の協調制御までを一貫して最適化する「電力インテリジェンス」の技術開発に取り組んでいます。
            </p>
          </div>
        </section>

        {/* Part 1: 技術思想 — 電力インテリジェンスとは何か */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              tag="POWER INTELLIGENCE"
              title="私たちが掲げる「電力インテリジェンス」とは"
              subtitle="発電設備・蓄電池・空調機器をバラバラの「モノ」として扱うのではなく、データと現場知見によって『導入前の判断』『最適な設備設計』『導入後の協調制御』を一貫してつなぐ技術アプローチです。"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
              <div className="rounded-3xl border border-surface-border bg-surface-slate p-7 sm:p-8">
                <div className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2">
                  従来のよくある課題
                </div>
                <h3 className="text-xl font-bold text-navy mb-4">
                  「設計」と「運用」が分断されている
                </h3>
                <ul className="space-y-3 text-sm text-navy-muted leading-relaxed">
                  <li className="flex items-start gap-2.5">
                    <span className="text-rose-500 font-bold">•</span>
                    <span>
                      屋根面積いっぱいに太陽光パネルを載せた結果、休日や中間期に電気が余ってしまい投資効率が悪化する。
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-rose-500 font-bold">•</span>
                    <span>
                      太陽光・蓄電池・デマンドコントローラー（空調制御）が別々に動き、現場が暑いのに蓄電池が余っているといったチグハグな運用になる。
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-rose-500 font-bold">•</span>
                    <span>
                      導入前の検討に何週間もかかり、そもそも自社施設に太陽光や蓄電池が向いているのか初期段階で判断しづらい。
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-3xl border border-spaq-cyan/40 bg-surface-sky p-7 sm:p-8">
                <div className="text-xs font-bold text-spaq-cyan-dark uppercase tracking-wider mb-2">
                  SPAQの技術アプローチ
                </div>
                <h3 className="text-xl font-bold text-navy mb-4">
                  需要に合う設計と、現場にやさしい協調制御をつなぐ
                </h3>
                <ul className="space-y-3 text-sm text-navy leading-relaxed">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-1" />
                    <span>
                      <strong>導入前の素早い相性診断（1minute solar）：</strong>
                      施設の電力需要パターンと屋根条件から、無駄なく自家消費できる適正規模を即座に見極めます。
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-1" />
                    <span>
                      <strong>現場目線の設備エンジニアリング：</strong>
                      3,000件以上の設計検討と2,000件以上の施工関与で培った知見をアルゴリズムと盤設計に反映し、安全性と採算性を両立します。
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-1" />
                    <span>
                      <strong>3要素のリアルタイム協調（SPAQ CORE）：</strong>
                      太陽光・空調・蓄電池を現場エッジで連携させ、室温や作業環境を守りながら30分デマンドピークを抑えます。
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Part 2: 2つの技術が生まれた背景 */}
        <section className="py-16 md:py-24 bg-surface-slate border-y border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              tag="WHY WE BUILT THEM"
              title="「1minute solar」と「SPAQ CORE」の開発背景"
              subtitle="SPAQの2つのコアプロダクトは、机上の理論ではなく、太陽光の設計・施工と施設運用の現場で直面した切実な課題から生まれました。"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-3xl border border-surface-border p-7 sm:p-8 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-spaq-gold-pale text-spaq-gold-dark text-xs font-bold mb-4">
                    <Sun className="w-4 h-4" />
                    <span>1minute solar の開発背景</span>
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-3">
                    「もっと手軽に、正直な導入判断ができる道具を作りたい」
                  </h3>
                  <p className="text-sm text-navy-muted leading-relaxed mb-4">
                    これまで数多くの発電所・自家消費設備の設計検討を行う中で、「詳細な図面を集めて何週間も待たないと概算すら分からない」「提案業者によって容量がバラバラで、自社にとって何が適正か判断できない」という声を数多く耳にしました。
                  </p>
                  <p className="text-sm text-navy-muted leading-relaxed">
                    そこで、長年培ってきた業種別の需要推計モデルと発電設計のノウハウをソフトウェアに凝縮し、初期段階から「載せすぎによる余剰ロス」を防ぎながら適正容量を比較できる『1minute solar』を開発しました。
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-surface-border">
                  <Button
                    to="/products/1minute-solar"
                    variant="outline-dark"
                    size="sm"
                    icon={<ArrowRight className="w-4 h-4" />}
                  >
                    1minute solar の詳細を見る
                  </Button>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-surface-border p-7 sm:p-8 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-spaq-cyan-pale text-spaq-cyan-dark text-xs font-bold mb-4">
                    <Cpu className="w-4 h-4" />
                    <span>SPAQ CORE の開発背景</span>
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-3">
                    「我慢を強いる省エネではなく、賢く補い合う制御を作りたい」
                  </h3>
                  <p className="text-sm text-navy-muted leading-relaxed mb-4">
                    太陽光や蓄電池、デマンドコントローラーを導入した施設でも、「夏場にデマコン警報が鳴って空調が一斉に止まり、現場が暑くて仕事にならない」「せっかく入れた蓄電池が肝心なピーク時に空になっている」という問題が頻発していました。
                  </p>
                  <p className="text-sm text-navy-muted leading-relaxed">
                    30分のデマンド時限の推移を秒単位で見守り、太陽光の自家消費・空調の緩和制御・蓄電池のピンポイント放電が互いに補い合う仕組みを実現するため、現場設置型の協調制御システム『SPAQ CORE』を開発しました。
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-surface-border">
                  <Button
                    to="/products/spaq-core"
                    variant="outline-dark"
                    size="sm"
                    icon={<ArrowRight className="w-4 h-4" />}
                  >
                    SPAQ CORE の詳細を見る
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Part 3: 現在提供している3つの技術基盤（設計・制御・検証） */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <SectionHeader
                tag="CURRENT ARCHITECTURE"
                title="現在提供している3つの技術基盤（設計・制御・検証）"
                subtitle="導入前の需要推計から、導入後の30分デマンド時限に同期したエッジ協調制御、そして実測データによる継続チューニングまでをひとつの技術サイクルとして実装しています。"
                className="mb-0"
              />
              <Badge variant="cyan">現在提供中のコア技術</Badge>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-7">
              {[
                {
                  label: '01｜設計（DESIGN）',
                  title: '需要曲線と日射特性のマッチング設計',
                  product: '1minute solar コアエンジン',
                  icon: <Compass className="w-6 h-6 text-spaq-gold-dark" />,
                  points: [
                    '業種・稼働カレンダー別の電力需要プロファイル推計アルゴリズム',
                    '地域気象データと屋根条件からの時間帯別発電シミュレーション',
                    '休日のベース負荷落ち込みや逆潮流制限を考慮した「余剰ロス最小化」容量算出',
                  ],
                },
                {
                  label: '02｜制御（CONTROL）',
                  title: '30分デマンド時限同期・3要素協調ロジック',
                  product: 'SPAQ CORE エッジ制御基盤',
                  icon: <Cpu className="w-6 h-6 text-spaq-cyan-dark" />,
                  points: [
                    '太陽光の自家消費 → 空調の緩和制御 → 蓄電池放電の優先順位づけ',
                    '蓄電池の放電余力と立ち上がり時間を考慮した、デマコン強制停止（60%制御・全止め）の抑止・許可連携',
                    '時限境界での不要な充放電ハンチングを防ぐ5段階運転方針と通信途絶時フェイルセーフ設計',
                  ],
                },
                {
                  label: '03｜検証（VERIFICATION）',
                  title: '実測データに基づく効果検証とチューニング',
                  product: 'モニタリング・分析サイクル',
                  icon: <ShieldCheck className="w-6 h-6 text-navy" />,
                  points: [
                    '受電点・太陽光PCS・蓄電池・空調のリアルタイム計測データ収集',
                    '「導入前の試算モデル」と「導入後の実測デマンド波形」のギャップ分析',
                    '季節変化や生産ライン・店舗稼働の変化に応じた目標デマンド値・制御閾値の継続見直し',
                  ],
                },
              ].map((tech, idx) => (
                <div
                  key={idx}
                  className="rounded-3xl border border-surface-border bg-surface-slate/60 p-7 shadow-soft flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-mono font-bold text-spaq-cyan-dark">
                        {tech.label}
                      </span>
                      <div className="w-11 h-11 rounded-2xl bg-white border border-surface-border flex items-center justify-center">
                        {tech.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-navy mb-1">{tech.title}</h3>
                    <div className="text-xs font-semibold text-spaq-gold-dark mb-4">
                      {tech.product}
                    </div>
                    <ul className="space-y-2.5 border-t border-surface-border pt-4">
                      {tech.points.map((pt, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-xs sm:text-sm text-navy-muted leading-relaxed"
                        >
                          <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-0.5" />
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Part 4: 今後の研究開発・共同検討テーマ */}
        <section className="py-16 md:py-24 bg-surface-sky border-t border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <SectionHeader
                tag="FUTURE R&D & COLLABORATION"
                title="今後の研究開発・共同検討テーマ"
                subtitle="現在当社が研究開発を進めているテーマ、および電力会社様・機器メーカー様・研究機関様との共同検証・応用検討を行っている将来領域です。"
                className="mb-0"
              />
              <Badge variant="gold">研究開発・共同検討フェーズ</Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: <BrainCircuit className="w-6 h-6 text-spaq-cyan-dark" />,
                  status: '研究開発中',
                  title: 'AI・気象連携による高精度な需要・発電予測',
                  desc: '過去の30分デマンド履歴と局地気象予測、施設の稼働スケジュールを組み合わせ、数時間先〜翌日のピーク発生確率をより高い精度で予測し、蓄電池の事前充放電計画を自動最適化する研究を進めています。',
                },
                {
                  icon: <Network className="w-6 h-6 text-spaq-cyan-dark" />,
                  status: '共同検討テーマ',
                  title: '電力会社・アグリゲーターとの需給調整連携（DR/VPP）',
                  desc: '施設内のピークカットにとどまらず、地域系統の需給逼迫や市場価格の変動に応じて、複数施設の蓄電池・空調リソースを無理のない範囲で協調させるデマンドレスポンス（DR）連携を検討しています。',
                },
                {
                  icon: <Sparkles className="w-6 h-6 text-spaq-gold-dark" />,
                  status: '共同検討テーマ',
                  title: '地域マイクログリッド・複数建物間の電力融通',
                  desc: '工業団地や物流拠点、自治体施設などにおいて、単一建物の枠を超えて太陽光余剰電力や蓄電リソースを融通し合うローカルエネルギーマネジメントへの応用を模索しています。',
                },
                {
                  icon: <Server className="w-6 h-6 text-spaq-gold-dark" />,
                  status: '応用可能性の検討',
                  title: '高負荷計算拠点・データセンター周辺の電力・空調協調',
                  desc: '今後電力需要の増大が見込まれるエッジ計算拠点や高負荷空調施設に対し、再生可能エネルギーの有効活用と冷却・電源の協調制御技術を応用する検討を行っています。',
                },
              ].map((rd, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-3xl border border-surface-border p-7 shadow-soft flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-11 h-11 rounded-2xl bg-surface-slate border border-surface-border flex items-center justify-center">
                        {rd.icon}
                      </div>
                      <Badge variant="gold" size="sm">{rd.status}</Badge>
                    </div>
                    <h3 className="text-lg font-bold text-navy mb-2">{rd.title}</h3>
                    <p className="text-sm text-navy-muted leading-relaxed">{rd.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-3xl bg-white border border-surface-border p-7 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-soft">
              <div className="space-y-1.5">
                <h3 className="text-lg font-bold text-navy">
                  技術連携・共同検証パートナーを募集しています
                </h3>
                <p className="text-xs sm:text-sm text-navy-muted">
                  デマンドコントローラーや蓄電池メーカー様、空調設備事業者様、小売電気事業者様との技術接続・実証フィールドでの共同検証に関するご相談を随時承っております。
                </p>
              </div>
              <Button
                to="/contact?type=partner"
                variant="navy"
                className="shrink-0"
                icon={<ArrowRight className="w-4 h-4" />}
              >
                技術連携・共同検討を相談する
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
